/**
 * VehiclePipelineStages — standalone stage pill navigation.
 *
 * Ported from fleet-admin's VehiclePipeline.tsx (VehiclePipelineStages export).
 * Same exact UI: "All" pill → chevron → individual stage pills with counts.
 */

import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import type { PipelineStage } from '@/lib/format';

interface VehiclePipelineStagesProps {
    stages: PipelineStage[];
    activeStage: string | null;
    onStageClick: (key: string | null) => void;
    groupCount: number;
}

export default function VehiclePipelineStages({
    stages,
    activeStage,
    onStageClick,
    groupCount,
}: VehiclePipelineStagesProps) {
    return (
        <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-hide">
            {/* Group "All" pill */}
            <button
                onClick={() => onStageClick(null)}
                className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200',
                    activeStage === null
                        ? 'bg-foreground text-background shadow-sm'
                        : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground',
                )}
            >
                All
                <span className={cn(
                    'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold',
                    activeStage === null
                        ? 'bg-background/20 text-background'
                        : 'bg-muted-foreground/15 text-muted-foreground',
                )}>
                    {groupCount}
                </span>
            </button>

            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />

            {/* Individual stage pills */}
            {stages.map((stage, idx) => {
                const isActive = activeStage === stage.key;
                const hasVehicles = stage.count > 0;

                return (
                    <div key={stage.key} className="flex items-center gap-1">
                        <button
                            onClick={() => onStageClick(stage.key)}
                            disabled={!hasVehicles && !isActive}
                            className={cn(
                                'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200',
                                isActive
                                    ? 'bg-foreground text-background shadow-sm ring-1 ring-foreground/20'
                                    : hasVehicles
                                        ? 'bg-muted border border-border text-foreground hover:bg-accent cursor-pointer'
                                        : 'bg-muted/30 text-muted-foreground/40 cursor-not-allowed',
                            )}
                        >
                            {stage.label}
                            <span className={cn(
                                'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold',
                                isActive
                                    ? 'bg-background/20 text-background'
                                    : hasVehicles
                                        ? 'bg-muted-foreground/15 text-muted-foreground'
                                        : 'bg-muted-foreground/10 text-muted-foreground/40',
                            )}>
                                {stage.count}
                            </span>
                        </button>
                        {idx < stages.length - 1 && (
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/30 shrink-0" />
                        )}
                    </div>
                );
            })}
        </div>
    );
}
